import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as classes from './SixthScreen.module.scss'
import cn from 'classnames'
import SectionTitle from '../../../ui/SectionTitle/SectionTitle'
import { Button } from '../../../ui/Button/Button'
import { useDataSixthScreenByHy } from './useDataSixthScreenByHy'
import { deleteHtmlTag } from '../../../../utils/deleteHtmlTag'
import { useDataSixthScreenByEn } from './useDataSixthScreenByEn'
import { Link, navigate } from 'gatsby'

const TRANSITION_DURATION = 1530
const AUTOPLAY_DELAY = 6000
const SWIPE_THRESHOLD = 40

function SixthScreen({ lang }) {
    const dataByHy = useDataSixthScreenByHy()
    const dataByEn = useDataSixthScreenByEn()

    const data = lang === 'hy' ? dataByHy : dataByEn
    const sectionTitle = useMemo(() => deleteHtmlTag(data.sectionTitle.value, 'p'), [])
    const title = useMemo(() => deleteHtmlTag(data.title.value, 'p').replace(/<br\s*\/?>/gi, ' '), [])
    const buttonText = useMemo(
        () => (data.buttonText?.value ? deleteHtmlTag(data.buttonText.value, 'p') : lang === 'hy' ? 'Ավելին' : 'Explore More'),
        []
    )

    const buttonUrl = lang === 'hy' ? `/projects/` : `/en/projects/`

    const slides = data.projects.nodes
    const slideCount = slides.length

    const [activeIndex, setActiveIndex] = useState(0)
    const [pendingIndex, setPendingIndex] = useState(null)
    const [slideDirection, setSlideDirection] = useState('next')
    const transitionTimeout = useRef(null)

    const slideNext = (direction = 'next') => {
        if (slideCount <= 1 || pendingIndex !== null) {
            return
        }

        const nextIndex =
            direction === 'prev'
                ? activeIndex === 0
                    ? slideCount - 1
                    : activeIndex - 1
                : activeIndex === slideCount - 1
                ? 0
                : activeIndex + 1

        setSlideDirection(direction === 'prev' ? 'prev' : 'next')
        setPendingIndex(nextIndex)

        transitionTimeout.current = setTimeout(() => {
            setActiveIndex(nextIndex)
            setPendingIndex(null)
        }, TRANSITION_DURATION)
    }

    useEffect(() => () => clearTimeout(transitionTimeout.current), [])

    const touchStart = useRef(null)
    const hasSwiped = useRef(false)

    const handleTouchStart = (event) => {
        const touch = event.touches[0]

        touchStart.current = { x: touch.clientX, y: touch.clientY }
        hasSwiped.current = false
    }

    const handleTouchEnd = (event) => {
        const start = touchStart.current
        const touch = event.changedTouches[0]

        touchStart.current = null

        if (!start || !touch) {
            return
        }

        const deltaX = touch.clientX - start.x
        const deltaY = touch.clientY - start.y

        if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) {
            return
        }

        hasSwiped.current = true
        slideNext(deltaX > 0 ? 'prev' : 'next')
    }

    const handleClickCapture = (event) => {
        if (hasSwiped.current) {
            event.preventDefault()
            event.stopPropagation()
        }
    }

    useEffect(() => {
        if (slideCount <= 1) {
            return
        }

        const intervalId = setInterval(() => {
            slideNext('next')
        }, AUTOPLAY_DELAY)

        return () => clearInterval(intervalId)
    }, [activeIndex, pendingIndex])

    const pointerIndex = pendingIndex ?? activeIndex

    const getTransitionProps = (index) => {
        if (pendingIndex !== null) {
            if (index === activeIndex) {
                return { 'data-animation': 'Y', 'data-direction': slideDirection, style: { zIndex: 3 } }
            }

            if (index === pendingIndex) {
                return { 'data-animation-next': 'Y', 'data-direction': slideDirection, style: { zIndex: 2 } }
            }

            return {}
        }

        return index === activeIndex ? { 'data-active': 'Y' } : {}
    }

    return (
        <div className={classes.Inner}>
            <div className={`${classes.AllContent} main-slider-content`}>
                <SectionTitle className={classes.SectionTitle} title={sectionTitle} lang={lang} light={true} />

                <div>
                    <h2 className={cn(classes.Title, { [classes.LangEN]: lang === 'en' })} dangerouslySetInnerHTML={{ __html: title }} />

                    {slides[pointerIndex] ? (
                        <p className={cn(classes.SlideTitle, classes.SlideTitleMain, 'main-slider-inner-name')}>
                            <span key={pointerIndex}>{slides[pointerIndex].title}</span>
                        </p>
                    ) : null}

                    <Button className={classes.Btn} onClick={() => navigate(buttonUrl)}>
                        {buttonText}
                    </Button>
                </div>
            </div>

            <div
                className={classes.Slides}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onClickCapture={handleClickCapture}
            >
                {slides.map((item, index) => (
                    <Link
                        key={index}
                        to={`${lang === 'en' ? '/en' : ''}/projects/${item.urlCode}/`}
                        className={classes.ImageWrap}
                        {...getTransitionProps(index)}
                    >
                        <div className={classes.Image} style={{ backgroundImage: `url(${item.previewPictureDesktop?.url})` }} />
                    </Link>
                ))}
            </div>

            <div className={classes.Dots}>
                {slides.map((item, index) => (
                    <div key={index} className={classes.Dot} data-active={index === pointerIndex ? 'Y' : null} />
                ))}
            </div>
        </div>
    )
}

export default SixthScreen
